/**
 * CRESiteIQ2 Map Helper Utilities
 * 
 * Utility functions for map operations including zoom calculations,
 * AGS value formatting, risk level badge classes, and debounce helper.
 */

import { ZOOM_LEVELS, ZOOM_LEVEL_ARRAY, RISK_LEVELS } from '../constants';

/**
 * Calculate zoom thumb position for zoom indicator
 * @param {number} currentZoom - Current zoom level
 * @param {number} minZoom - Minimum zoom level
 * @param {number} maxZoom - Maximum zoom level
 * @returns {number} - Thumb position as percentage (0-100)
 */
export function calculateZoomThumbPosition(
    currentZoom,
    minZoom = ZOOM_LEVELS.MIN,
    maxZoom = ZOOM_LEVELS.MAX
) {
    if (currentZoom < minZoom) currentZoom = minZoom;
    if (currentZoom > maxZoom) currentZoom = maxZoom;

    const range = maxZoom - minZoom;
    const position = ((currentZoom - minZoom) / range) * 100;

    return Math.round(position);
}

/**
 * Get the active zoom level configuration based on current zoom
 * @param {number} currentZoom - Current zoom level
 * @returns {Object|null} - Zoom level configuration object or null
 */
export function getActiveZoomLevel(currentZoom) {
    return ZOOM_LEVEL_ARRAY.find(level =>
        currentZoom >= level.min && currentZoom <= level.max
    ) || null;
}

/**
 * Check if a layer is available at the current zoom level
 * @param {Object} layer - Layer configuration object
 * @param {number} currentZoom - Current zoom level
 * @returns {boolean} - True if layer is available
 */
export function isLayerAvailable(layer, currentZoom) {
    if (!layer || !layer.minZoom) return true;

    const minZoom = layer.minZoom || ZOOM_LEVELS.MIN;
    const maxZoom = layer.maxZoom || ZOOM_LEVELS.MAX;

    return currentZoom >= minZoom && currentZoom <= maxZoom;
}

/**
 * Format AGS value based on variable type
 * @param {number} value - Raw value to format
 * @param {string} variableType - Type of variable (currency, percent, number, etc.)
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted value string
 */
export function formatAgsValue(value, variableType, decimals = 2) {
    if (value === null || value === undefined || isNaN(value)) {
        return 'N/A';
    }

    const numValue = parseFloat(value);

    switch (variableType?.toLowerCase()) {
        case 'currency':
        case 'dollar':
        case 'dollars':
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(numValue);

        case 'percent':
        case 'percentage':
            return `${numValue.toFixed(decimals)}%`;

        case 'decimal':
            return numValue.toFixed(decimals);

        case 'integer':
        case 'count':
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(Math.round(numValue));

        default:
            // Default to number with commas
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: decimals
            }).format(numValue);
    }
}

/**
 * Get risk level badge class based on risk value
 * @param {number} riskLevel - Risk level value (1-5)
 * @returns {string} - CSS class string for badge styling
 */
export function getRiskLevelBadgeClass(riskLevel) {
    const level = parseInt(riskLevel);

    switch (level) {
        case 1:
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
        case 2:
            return 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200';
        case 3:
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        case 4:
            return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
        case 5:
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
}

/**
 * Get risk level configuration by value
 * @param {number} riskLevel - Risk level value (1-5)
 * @returns {Object} - Risk level configuration object
 */
export function getRiskLevelConfig(riskLevel) {
    const level = parseInt(riskLevel);

    switch (level) {
        case 1:
            return RISK_LEVELS.VERY_LOW;
        case 2:
            return RISK_LEVELS.LOW;
        case 3:
            return RISK_LEVELS.MODERATE;
        case 4:
            return RISK_LEVELS.HIGH;
        case 5:
            return RISK_LEVELS.VERY_HIGH;
        default:
            return { label: 'Unknown', value: 0, color: '#6b7280' };
    }
}

/**
 * Debounce function to limit function execution rate
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export function debounce(func, wait) {
    let timeout;

    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };

        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Throttle function to limit function execution rate
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} - Throttled function
 */
export function throttle(func, limit) {
    let inThrottle;

    return function executedFunction(...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
}

/**
 * Calculate map bounds from center and zoom level
 * @param {Object} center - Map center { lat, lng }
 * @param {number} zoom - Zoom level
 * @param {number} width - Map width in pixels
 * @param {number} height - Map height in pixels
 * @returns {Object} - Bounds object { north, south, east, west }
 */
export function calculateBounds(center, zoom, width = 1000, height = 600) {
    const WORLD_DIM = { height: 256, width: 256 };

    function zoom2scale(zoom) {
        return Math.pow(2, zoom);
    }

    const scale = zoom2scale(zoom);
    const worldCoordWidth = width / scale;
    const worldCoordHeight = height / scale;

    const centerLat = center.lat;
    const centerLng = center.lng;

    const latOffset = (worldCoordHeight / WORLD_DIM.height) * 180;
    const lngOffset = (worldCoordWidth / WORLD_DIM.width) * 360;

    return {
        north: Math.min(90, centerLat + latOffset),
        south: Math.max(-90, centerLat - latOffset),
        east: centerLng + lngOffset,
        west: centerLng - lngOffset
    };
}

/**
 * Generate color scale for data visualization
 * @param {Array} data - Array of data values
 * @param {Array} colors - Array of color hex codes
 * @param {boolean} reverse - Whether to reverse the color scale
 * @returns {Array} - Array of color scale items { color, label, value, range }
 */
export function generateColorScale(data, colors, reverse = false) {
    if (!data || data.length === 0) return [];

    const values = data.map(d => parseFloat(d)).filter(v => !isNaN(v));
    if (values.length === 0) return [];

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    const step = range / (colors.length - 1);

    let scale = colors.map((color, index) => {
        const value = min + (step * index);
        return {
            color,
            label: formatAgsValue(value, 'number', 0),
            value,
            range: {
                min: index === 0 ? min : min + (step * index),
                max: index === colors.length - 1 ? max : min + (step * (index + 1))
            }
        };
    });

    if (reverse) {
        scale = scale.reverse();
    }

    return scale;
}

/**
 * Get layer icon gradient based on color
 * @param {string} color - Color name (green, blue, etc.)
 * @returns {string} - CSS gradient string
 */
export function getLayerIconGradient(color) {
    const gradients = {
        green: 'linear-gradient(135deg, #10b981, #059669)',
        blue: 'linear-gradient(135deg, #3b82f6, #2563eb)',
        purple: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
        orange: 'linear-gradient(135deg, #f97316, #ea580c)',
        red: 'linear-gradient(135deg, #ef4444, #dc2626)',
        yellow: 'linear-gradient(135deg, #eab308, #ca8a04)'
    };

    return gradients[color] || gradients.blue;
}

/**
 * Check if device is mobile based on screen width
 * @returns {boolean} - True if mobile device
 */
export function isMobileDevice() {
    return window.innerWidth < 768;
}

/**
 * Check if device is tablet based on screen width
 * @returns {boolean} - True if tablet device
 */
export function isTabletDevice() {
    return window.innerWidth >= 768 && window.innerWidth < 1024;
}

/**
 * Check if device is desktop based on screen width
 * @returns {boolean} - True if desktop device
 */
export function isDesktopDevice() {
    return window.innerWidth >= 1024;
}

/**
 * Get responsive control panel width
 * @returns {string|number} - Width value (number for px, string for %)
 */
export function getControlPanelWidth() {
    if (isMobileDevice()) return '100%';
    if (isTabletDevice()) return 320;
    return 384;
}

/**
 * Convert hex color to rgba
 * @param {string} hex - Hex color code
 * @param {number} alpha - Alpha value (0-1)
 * @returns {string} - RGBA color string
 */
export function hexToRgba(hex, alpha = 1) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Clamp a value between min and max
 * @param {number} value - Value to clamp
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} - Clamped value
 */
export function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

/**
 * Format large numbers with K, M, B suffixes
 * @param {number} num - Number to format
 * @returns {string} - Formatted string
 */
export function formatLargeNumber(num) {
    if (num >= 1000000000) {
        return (num / 1000000000).toFixed(1) + 'B';
    }
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
}
